import { useEffect, useState } from "react";
import ConfirmationModal from "../components/ConfirmationModal";
import CurrentAopStatus from "../components/CurrentAopStatus";
import { TableActionColumnHeader } from "../components/TableShell";
import { useNavigate } from "react-router-dom";
import { IconEdit, IconDuplicate, IconDelete, IconLeads, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { buildDuplicateLabel } from "../shared/utils/duplicateLabel";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";
import { fetchCurrentAopProgramNames } from "../shared/api/currentAopByProgram";
import {
  CURRENT_AOP_COLUMN_LABEL,
  getProgramCurrentAopMembership,
} from "../shared/utils/currentAopByProgram";
import { parseProgramColor } from "../shared/utils/programColor";
import { ProgramColorSwatch } from "../components/ProgramColorInput";
import ProgramEditorModal from "../components/ProgramEditorModal";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

function Programs() {
  const navigate = useNavigate();
  const [currentObjs, setCurrentObjs] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [programModal, setProgramModal] = useState({ open: false, mode: 'create', program: null });
  const [bannerError, setBannerError] = useState('');
  /** null = not loaded yet; Set of program names in Current AOP By Program */
  const [aopProgramNames, setAopProgramNames] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  useEffect(() => {
    getDocument();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchCurrentAopProgramNames({ serverUrl, account_id })
      .then((names) => {
        if (!cancelled) setAopProgramNames(names);
      })
      .catch((err) => {
        if (!cancelled) {
          setBannerError(getMessageForFetchError(err, "Failed to load Current AOP programs."));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [serverUrl, account_id]);

  function getDocument() {
    apiFetch(serverUrl + '/getProgram', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function openProgramCreateModal() {
    setImportPanelExpanded(false);
    setProgramModal({ open: true, mode: 'create', program: null });
  }

  function openProgramEditModal(program) {
    setImportPanelExpanded(false);
    setProgramModal({ open: true, mode: 'edit', program });
  }

  function closeProgramModal() {
    setProgramModal({ open: false, mode: 'create', program: null });
  }

  async function saveProgramFromModal(payload) {
    const isEdit = Boolean(payload._id);
    const response = await apiFetch(serverUrl + (isEdit ? '/updateProgram' : '/createProgram'), {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        ...(isEdit ? { _id: payload._id } : {}),
        name: payload.name,
        description: payload.description,
        category: payload.category,
        color: payload.color,
      }),
    });
    const text = await response.text();
    if (!response.ok) {
      throw new Error(text || 'Failed to save program.');
    }
    closeProgramModal();
    getDocument();
    setConfirmDialog({ open: true, title: "Program", message: text, mode: "alert", onConfirm: null });
  }

  function duplicateDocument(currentObj) {
    const { name: n, description: d, category: c, color: col } = currentObj;
    const colorParsed = parseProgramColor(col);
    apiFetch(serverUrl + '/createProgram', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        name: buildDuplicateLabel(n, { fallback: 'Program' }),
        description: d || '',
        category: c || '',
        color: colorParsed.ok ? colorParsed.color : '',
      }),
    })
      .then(response => response.text())
      .then(data => {
        setConfirmDialog({ open: true, title: "Program", message: data, mode: "alert", onConfirm: null });
        getDocument();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function deleteDocument(currentObj) {
    const { _id } = currentObj;
    apiFetch(serverUrl + '/deleteProgram', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id }),
    })
      .then(response => response.text())
      .then(data => {
        setConfirmDialog({ open: true, title: "Program", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(_id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function parseCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const nameIndex = findCsvColumnIndex(headers, ['name', 'program']);
    const descriptionIndex = findCsvColumnIndex(headers, ['description', 'details']);
    const categoryIndex = findCsvColumnIndex(headers, ['category', 'group']);
    const colorIndex = findCsvColumnIndex(headers, ['color', 'colour']);
    if (nameIndex === -1) throw new Error('CSV must contain column: name (or program)');
    const records = [];
    for (const values of rows) {
      if (values.length > nameIndex) {
        records.push({
          name: values[nameIndex] || '',
          description: descriptionIndex >= 0 ? (values[descriptionIndex] || '') : '',
          category: categoryIndex >= 0 ? (values[categoryIndex] || '') : '',
          color: colorIndex >= 0 ? (values[colorIndex] || '') : '',
        });
      }
    }
    return records;
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (!record.name) {
          errorCount++;
          errors.push(`Row ${i + 2}: Name required`);
          continue;
        }
        const colorParsed = parseProgramColor(record.color);
        if (!colorParsed.ok) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${colorParsed.error}`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + '/createProgram', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              name: record.name,
              description: record.description || '',
              category: record.category || '',
              color: colorParsed.color,
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }
      let message = `Import completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`;
      if (errors.length > 0 && errors.length <= 10) message += '\n\nErrors:\n' + errors.join('\n');
      else if (errors.length > 10) message += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
      setConfirmDialog({ open: true, title: "Import completed", message, mode: "alert", onConfirm: null });
      getDocument();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, 'Error importing CSV.'));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile('program-sample.csv', [
      'name,description,category,color',
      'Team Alpha,Core development team,Sports,#312e81',
      'Team Beta,QA and support,Education,#4f46e5',
      'Events Committee,Event planning and coordination,Events,',
    ]);
  }

  function handleSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  }

  function getFilteredAndSortedData() {
    let filtered = (currentObjs || []).filter(obj => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchLower || (
        (obj.name && obj.name.toLowerCase().includes(searchLower)) ||
        (obj.description && obj.description.toLowerCase().includes(searchLower)) ||
        (obj.category && obj.category.toLowerCase().includes(searchLower))
      );
      const matchesCategory = !categoryFilter || (obj.category || '') === categoryFilter;
      return matchesSearch && matchesCategory;
    });
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue;
        let bValue;
        if (sortConfig.key === 'currentAop') {
          const aIn = getProgramCurrentAopMembership(a.name, aopProgramNames);
          const bIn = getProgramCurrentAopMembership(b.name, aopProgramNames);
          // Yes first when asc: true > false > null
          aValue = aIn === true ? 2 : aIn === false ? 1 : 0;
          bValue = bIn === true ? 2 : bIn === false ? 1 : 0;
        } else {
          aValue = a[sortConfig.key] ?? '';
          bValue = b[sortConfig.key] ?? '';
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function handleSelectAll(e) {
    const filteredData = getFilteredAndSortedData();
    const newSelectedIds = new Set(selectedIds);
    if (e.target.checked) filteredData.forEach(obj => newSelectedIds.add(obj._id));
    else filteredData.forEach(obj => newSelectedIds.delete(obj._id));
    setSelectedIds(newSelectedIds);
  }

  function handleSelectOne(id, checked) {
    const newSelectedIds = new Set(selectedIds);
    if (checked) newSelectedIds.add(id);
    else newSelectedIds.delete(id);
    setSelectedIds(newSelectedIds);
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete programs", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete programs",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        let successCount = 0;
        let errorCount = 0;
        const errors = [];
        for (let i = 0; i < idsToDelete.length; i++) {
          const _id = idsToDelete[i];
          try {
            const response = await apiFetch(serverUrl + '/deleteProgram', {
              method: 'POST',
              body: JSON.stringify({ account_id, _id }),
            });
            const data = await response.text();
            if (response.ok) successCount++;
            else {
              errorCount++;
              errors.push(`ID ${_id}: ${data}`);
            }
          } catch (error) {
            errorCount++;
            errors.push(`ID ${_id}: ${getMessageForFetchError(error)}`);
          }
        }
        let message = `Delete completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`;
        if (errors.length > 0 && errors.length <= 10) message += '\n\nErrors:\n' + errors.join('\n');
        else if (errors.length > 10) message += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message,
          onConfirm: null,
        });
        setSelectedIds(new Set());
        getDocument();
        setDeleting(false);
      },
    });
  }

  const categoryOptions = [...new Set((currentObjs || []).map((o) => (o.category || '').trim()).filter(Boolean))].sort();
  const filteredRows = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <div className="page-content">
      <div className="page-top">
        <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || "Confirm"}
          message={confirmDialog.message}
          mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
          confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
          onConfirm={() => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() =>
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
          }
        />
        <ProgramEditorModal
          open={programModal.open}
          mode={programModal.mode}
          program={programModal.program}
          categoryOptions={categoryOptions}
          onCancel={closeProgramModal}
          onSubmit={saveProgramFromModal}
        />
        <ImportCsvPanel
          expanded={importPanelExpanded}
          fileInputId="csv-import-programs"
          importing={importing}
          columnHint="Columns: name (or program), description, category, color (optional; #RRGGBB)"
          onDownloadSample={downloadSampleCSV}
          onFileChange={handleFileChange}
          onImportText={importFromCsvText}
        />
        <div className="toolbar">
          <input type="text" id="search-input-programs" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search name, description, category…" className="input input-search" />
          <button type="button" onClick={() => setSearchTerm('')} className="btn btn-sm btn-secondary">Clear</button>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="input input-min-120" title="Filter by category">
            <option value="">All categories</option>
            {categoryOptions.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <span className="toolbar-divider" />
          <button type="button" onClick={() => { setImportPanelExpanded(!importPanelExpanded); if (programModal.open) closeProgramModal(); }} className={`btn btn-md ${importPanelExpanded ? 'btn-primary-outline active' : 'btn-primary'}`}>{importPanelExpanded ? '− Import CSV' : '+ Import CSV'}</button>
          <button type="button" onClick={openProgramCreateModal} className="btn btn-md btn-primary">+ New</button>
          <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0 || deleting} className="btn btn-danger">Delete{selectedCountSuffix}</button>
        </div>
      </div>
      <div className="table-scroll">
        <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th><input type="checkbox" checked={(() => { const d = getFilteredAndSortedData(); return d.length > 0 && d.every(obj => selectedIds.has(obj._id)); })()} onChange={handleSelectAll} disabled={getFilteredAndSortedData().length === 0} /></th>
              <th className="sortable" onClick={() => handleSort('name')}>Name</th>
              <th className="sortable" onClick={() => handleSort('description')}>Description</th>
              <th className="sortable" onClick={() => handleSort('category')}>Category</th>
              <th className="sortable" onClick={() => handleSort('currentAop')} title="Program is in Current AOP By Program (Operating Plan Current FY)">{CURRENT_AOP_COLUMN_LABEL}</th>
              <th className="sortable table-cell-color" onClick={() => handleSort('color')}>Color</th>
              <TableActionColumnHeader align="left" />
            </tr>
          </thead>
          <tbody>
            {getFilteredAndSortedData().map(currentObj => {
              const inCurrentAop = getProgramCurrentAopMembership(currentObj.name, aopProgramNames);
              return (
                <tr key={currentObj._id}>
                  <td>
                    <input type="checkbox" checked={selectedIds.has(currentObj._id)} onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)} />
                  </td>
                  <td>
                    <button type="button" onClick={() => navigate(`/ProgramManagement?tab=programLead&program=${encodeURIComponent(currentObj._id)}`)} className="btn-link block">{currentObj.name}</button>
                  </td>
                  <td className="table-cell-ellipsis" title={currentObj.description}>{currentObj.description || '—'}</td>
                  <td className="table-cell-ellipsis-sm" title={currentObj.category}>{currentObj.category || '—'}</td>
                  <td><CurrentAopStatus inCurrentAop={inCurrentAop} /></td>
                  <td className="table-cell-color">
                    <ProgramColorSwatch color={currentObj.color} />
                  </td>
                  <td>
                    <RowActionIcons>
                      <button type="button" onClick={() => navigate(`/ProgramManagement?tab=programLead&program=${encodeURIComponent(currentObj._id)}`)} className="btn-link" style={iconBtnStyle} title="Leads"><IconLeads size={18} /></button>
                      <button type="button" onClick={() => openProgramEditModal(currentObj)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                      <button type="button" onClick={() => duplicateDocument(currentObj)} className="btn-link" style={iconBtnStyle} title="Duplicate"><IconDuplicate size={18} /></button>
                      <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                    </RowActionIcons>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
export default Programs;
